import { useEffect, useState } from 'react';
import {
  deleteMelodyPattern,
  loadMelodyLibrary,
  saveMelodyPattern,
  type SavedMelodyPattern,
} from '../utils/melodyLibrary';
import { patternHasNotes } from '../utils/pattern';
import { accompanimentHasChords } from '../utils/chordAccompaniment';
import type { ChordAccompaniment, DrumPattern, Pattern } from '../types';

interface Props {
  open: boolean;
  bpm: number;
  pattern: Pattern;
  drumPattern: DrumPattern;
  chordAccompaniment: ChordAccompaniment;
  loadedId: string | null;
  onClose: () => void;
  onLoad: (entry: SavedMelodyPattern) => void;
  onSaved: (entry: SavedMelodyPattern) => void;
  onDeleted?: (id: string) => void;
}

export function MelodyLibraryPanel({
  open,
  bpm,
  pattern,
  drumPattern,
  chordAccompaniment,
  loadedId,
  onClose,
  onLoad,
  onSaved,
  onDeleted,
}: Props) {
  const [items, setItems] = useState<SavedMelodyPattern[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setItems(loadMelodyLibrary());
    if (loadedId) {
      const current = loadMelodyLibrary().find((entry) => entry.id === loadedId);
      setTitle(current?.title ?? '');
    } else {
      setTitle('');
    }
    setMessage(null);
  }, [open, loadedId]);

  if (!open) return null;

  const refresh = () => setItems(loadMelodyLibrary());

  const handleSave = (asNew: boolean) => {
    if (!title.trim()) {
      setMessage('Escribe un nombre para el patrón.');
      return;
    }
    if (!patternHasNotes(pattern)) {
      setMessage('Pinta al menos una nota antes de guardar.');
      return;
    }

    const entry = saveMelodyPattern({
      id: asNew ? undefined : loadedId ?? undefined,
      title,
      bpm,
      pattern,
      drumPattern,
      chordAccompaniment,
    });

    refresh();
    onSaved(entry);
    setMessage(asNew ? '¡Patrón guardado en la biblioteca!' : '¡Patrón actualizado!');
    window.setTimeout(() => setMessage(null), 2500);
  };

  const handleDelete = (id: string) => {
    deleteMelodyPattern(id);
    refresh();
    onDeleted?.(id);
  };

  return (
    <div className="melody-library-backdrop" role="presentation" onClick={onClose}>
      <div
        className="melody-library-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="melody-library-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="melody-library-panel__header">
          <div>
            <h2 id="melody-library-title" className="melody-library-panel__title">
              Biblioteca de patrones
            </h2>
            <p className="melody-library-panel__subtitle">
              Guarda y reutiliza tus secuencias de boomwhackers.
            </p>
          </div>
          <button
            type="button"
            className="melody-library-panel__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <section className="melody-library-panel__save">
          <label className="melody-library-panel__field">
            Nombre del patrón
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej. Do-Re-Mi compás 1"
              maxLength={60}
            />
          </label>
          <div className="melody-library-panel__save-actions">
            {loadedId ? (
              <>
                <button type="button" className="btn btn-secondary" onClick={() => handleSave(false)}>
                  Actualizar
                </button>
                <button type="button" className="btn btn-play" onClick={() => handleSave(true)}>
                  Guardar como nuevo
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-play" onClick={() => handleSave(true)}>
                Guardar patrón
              </button>
            )}
          </div>
          {message && <p className="melody-library-panel__message">{message}</p>}
        </section>

        <div className="melody-library-panel__list-wrap">
          {items.length === 0 ? (
            <p className="melody-library-panel__empty">
              Aún no hay patrones guardados. Crea uno y pulsa Guardar patrón.
            </p>
          ) : (
            <ul className="melody-library-list">
              {items.map((entry) => (
                <li key={entry.id} className="melody-library-list__item">
                  <button
                    type="button"
                    className={`melody-library-card ${loadedId === entry.id ? 'melody-library-card--active' : ''}`}
                    onClick={() => {
                      onLoad(entry);
                      onClose();
                    }}
                  >
                    <h3 className="melody-library-card__title">{entry.title}</h3>
                    <p className="melody-library-card__desc">{entry.description}</p>
                    <span className="melody-library-card__meta">
                      {entry.pattern.measures.length} compás(es) · {entry.bpm} BPM
                      {entry.drumPattern?.enabled ? ' · 🥁' : ''}
                      {entry.chordAccompaniment?.enabled &&
                      accompanimentHasChords(entry.chordAccompaniment)
                        ? ' · ✨'
                        : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="melody-library-card__delete"
                    onClick={() => handleDelete(entry.id)}
                    aria-label={`Eliminar ${entry.title}`}
                    title="Eliminar"
                  >
                    🗑
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
